import logEvent from 'analytics/logEvent';
import cn from 'classnames';
import { QueryParser } from 'domains/common/queryParser';
import useTranslationAndTypograph from 'hooks/useTranslationAndTypograph';
import Image from 'next/image';
import { useRouter } from 'next/router';
import React from 'react';

import Button from 'components/basic/Button';
import { BodyL, H1 } from 'components/basic/Typography';
import Page from 'components/layouts/Page';
import { errorsTexts } from 'components/pagesComponents/payments/PaymentFailedPage/constants';

import { useAppDispatch } from 'redux-store';
import { setChargeProcessingStatus } from 'redux-store/slices/purchases';

import s from './PaymentFailedPage.module.scss';

interface Props {
  details?: string;
  redirectUrl?: string;
}

export default function PaymentFailedPage({
  details = 'default',
  redirectUrl,
}: Props): JSX.Element {
  const t = useTranslationAndTypograph();
  const dispatch = useAppDispatch();
  const router = useRouter();
  const token = router.query.token as string | undefined;
  const compact = router.query.compact as string | undefined;
  const page = router.query.page as string;
  let errorText = errorsTexts[details];

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!errorText) {
    errorText = {
      title: 'Can’t process your payment',
      text: 'Please try again. If the problem persists, message us via the in-app chat or at support@welltory.com',
    };
  }

  let redirectPath = redirectUrl;
  if (!redirectPath && page) {
    const search = new QueryParser(window.location.search).set({
      token,
      compact,
    });
    redirectPath = `/payments/plans/${page}/${search.toString()}`;
  }

  return (
    <Page>
      <H1 className="text-center mb-4 cy-error-title">{t(errorText.title)}</H1>
      <BodyL className={cn(s.h4, 'text-center mb-10 cy-error-test')}>
        {t(errorText.text)}
      </BodyL>
      <div className={s.imageWrapper}>
        <Image
          width={718}
          height={272}
          src="/pagesImages/payments/PaymentFailedPage/paymentError.jpg"
        />
      </div>
      <div className={cn(s.buttonWrapper, 'mt-10')}>
        <Button
          id="return"
          look="secondary"
          className="cy-error-button"
          onClick={(): void => {
            logEvent('Web_Payment_Failed_Page_Button_Clicked', {
              button_id: 'return',
            });
            dispatch(
              setChargeProcessingStatus({ status: 'notStarted', details: '' }),
            );
            if (redirectPath) {
              window.location.href = redirectPath;
            }
          }}
        >
          {t('go back')}
        </Button>
      </div>
    </Page>
  );
}
